import React, { useState } from 'react';
import { 
    Box, Typography, Container, Grid, Card, CardContent, 
    Paper, useTheme, Chip, Switch, FormControlLabel,
    Button, TextField, Select, MenuItem, InputLabel, 
    FormControl, Divider, IconButton, Tooltip, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import { 
    NotificationsActive as NotificationsActiveIcon,
    AddCircleOutline as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Timer as TimerIcon,
    Message as MessageIcon,
    SettingsSuggest as SettingsSuggestIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon
} from '@mui/icons-material';

// --- MOCK DATA ---
const initialRules = [
    {
        id: 1,
        name: 'Critical CPU Load',
        metric: 'CPU Utilization',
        condition: '>',
        threshold: 90,
        unit: '%',
        period: '5m',
        action: 'Email Admins',
        status: true,
        lastTrigger: '2025-10-01 14:30 UTC'
    },
    {
        id: 2,
        name: 'High API Latency',
        metric: 'API Latency',
        condition: '>',
        threshold: 200,
        unit: 'ms',
        period: '1m',
        action: 'Slack Dev Channel',
        status: true,
        lastTrigger: 'N/A'
    },
    {
        id: 3,
        name: 'Low Cache Hit Ratio',
        metric: 'Cache Hit Ratio',
        condition: '<',
        threshold: 75,
        unit: '%',
        period: '15m',
        action: 'Trigger Auto-Scale',
        status: false,
        lastTrigger: '2025-09-28 09:00 UTC'
    },
];

const metricsOptions = [
    'CPU Utilization', 'Memory Usage', 'DB Connection Pool', 
    'API Latency', 'Cache Hit Ratio', 'Request Queue Depth'
];

const actionOptions = [
    'Email Admins', 'Slack Dev Channel', 'PagerDuty Alert', 
    'Trigger Auto-Scale', 'Restart Service'
];


// --- RULE MANAGEMENT COMPONENT ---
const RuleForm = ({ initialRule, onSave, onCancel }) => {
    const [rule, setRule] = useState(initialRule || {
        name: '', metric: metricsOptions[0], condition: '>', 
        threshold: '', unit: '', period: '1m', action: actionOptions[0], status: true
    });
    const theme = useTheme();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setRule(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        onSave(rule);
    };

    return (
        <Card elevation={6} sx={{ p: 3, mb: 4, bgcolor: theme.palette.background.default }}>
            <Typography variant="h6" gutterBottom fontWeight={700} color="primary">
                {initialRule ? 'Edit Alert Rule' : 'Create New Alert Rule'}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Rule Name"
                        name="name"
                        value={rule.name}
                        onChange={handleChange}
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Alert Action</InputLabel>
                        <Select
                            label="Alert Action"
                            name="action"
                            value={rule.action}
                            onChange={handleChange}
                        >
                            {actionOptions.map(action => (
                                <MenuItem key={action} value={action}>{action}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                        Condition: When 
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                        {/* Metric */}
                        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                            <InputLabel>Metric</InputLabel>
                            <Select
                                label="Metric"
                                name="metric"
                                value={rule.metric}
                                onChange={handleChange}
                            >
                                {metricsOptions.map(metric => (
                                    <MenuItem key={metric} value={metric}>{metric}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Condition */}
                        <FormControl variant="outlined" sx={{ width: 80 }}>
                            <InputLabel>Op</InputLabel>
                            <Select
                                label="Op"
                                name="condition"
                                value={rule.condition}
                                onChange={handleChange}
                            >
                                <MenuItem value=">">&gt;</MenuItem>
                                <MenuItem value="<">&lt;</MenuItem>
                                <MenuItem value="=">=</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Threshold */}
                        <TextField
                            label="Threshold"
                            name="threshold"
                            value={rule.threshold}
                            onChange={handleChange}
                            type="number"
                            variant="outlined"
                            sx={{ width: 150 }}
                        />

                        {/* Unit */}
                        <TextField
                            label="Unit"
                            name="unit"
                            value={rule.unit}
                            onChange={handleChange}
                            variant="outlined"
                            sx={{ width: 80 }}
                        />

                        {/* Period */}
                        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                            <InputLabel>For Period</InputLabel>
                            <Select
                                label="For Period"
                                name="period"
                                value={rule.period}
                                onChange={handleChange}
                            >
                                <MenuItem value="1m">1 minute</MenuItem>
                                <MenuItem value="5m">5 minutes</MenuItem>
                                <MenuItem value="15m">15 minutes</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
                
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                        <Button variant="outlined" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSubmit} startIcon={<SettingsSuggestIcon />}>
                            {initialRule ? 'Update Rule' : 'Save Rule'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
};


// --- MAIN COMPONENT ---
const AutomatedAlertsTriggers = () => {
    const [rules, setRules] = useState(initialRules);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const theme = useTheme();

    const handleToggleStatus = (id) => {
        setRules(prev => prev.map(rule => 
            rule.id === id ? { ...rule, status: !rule.status } : rule
        ));
    };

    const handleDelete = (id) => {
        setRules(prev => prev.filter(rule => rule.id !== id));
    };

    const handleEdit = (rule) => {
        setEditingRule(rule);
        setIsFormOpen(true);
    };

    const handleSave = (newRule) => {
        if (editingRule) {
            // Update existing rule
            setRules(prev => prev.map(rule => 
                rule.id === newRule.id ? newRule : rule
            ));
        } else {
            // Create new rule
            // FIX: Calculate newId inside the setRules callback to access 'prev'
            setRules(prev => {
                const newId = Math.max(...prev.map(r => r.id), 0) + 1;
                return [...prev, { ...newRule, id: newId, lastTrigger: 'N/A' }];
            });
        }
        setIsFormOpen(false);
        setEditingRule(null);
    };

    const handleCancel = () => {
        setIsFormOpen(false);
        setEditingRule(null);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.main, 
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <NotificationsActiveIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.error.main }} />
                Automated Alerts & Triggers
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Define and manage automated system alert rules based on real-time metrics.
            </Typography>

            {/* Rule Management */}
            <Box mb={4} display="flex" justifyContent="flex-end">
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AddIcon />}
                    onClick={() => { setEditingRule(null); setIsFormOpen(true); }}
                >
                    New Alert Rule
                </Button>
            </Box>

            {isFormOpen && (
                <RuleForm 
                    initialRule={editingRule} 
                    onSave={handleSave} 
                    onCancel={handleCancel} 
                />
            )}

            {/* Alerts Table */}
            <Paper elevation={6} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} mb={3}>
                    Active Alert Rules ({rules.length})
                </Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: theme.palette.action.hover } }}>
                                <TableCell>Rule Name</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell>Condition</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Last Trigger</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rules.map((rule) => (
                                <TableRow key={rule.id} hover>
                                    <TableCell component="th" scope="row">
                                        <Typography variant="subtitle1" fontWeight={600}>{rule.name}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    checked={rule.status} 
                                                    onChange={() => handleToggleStatus(rule.id)} 
                                                    color={rule.status ? "success" : "default"} 
                                                />
                                            }
                                            label={
                                                <Chip 
                                                    label={rule.status ? "Active" : "Disabled"} 
                                                    color={rule.status ? "success" : "default"} 
                                                    size="small"
                                                    icon={rule.status ? <CheckCircleIcon /> : <ErrorIcon />}
                                                />
                                            }
                                            labelPlacement="top"
                                            sx={{ m: 0 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={`${rule.metric} ${rule.condition} ${rule.threshold}${rule.unit}`}
                                            icon={<TimerIcon fontSize="small" />}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                                            for {rule.period}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={rule.action}
                                            icon={<MessageIcon fontSize="small" />}
                                            size="small"
                                            color="secondary"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color={rule.lastTrigger === 'N/A' ? 'textSecondary' : 'error'}>
                                            {rule.lastTrigger}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit Rule">
                                            <IconButton color="primary" size="small" onClick={() => handleEdit(rule)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Rule">
                                            <IconButton color="error" size="small" onClick={() => handleDelete(rule.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {rules.length === 0 && (
                    <Box textAlign="center" py={5}>
                        <Typography variant="h6" color="textSecondary">
                            No Alert Rules Defined. Click "New Alert Rule" to begin.
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default AutomatedAlertsTriggers;